import React, { useState, useEffect, useRef } from 'react';
import { supabase } from '../../lib/supabase';
import { PlusCircle, Save, X, Trash2, AlertCircle, Download, Upload, Star } from 'lucide-react';
import { VideoUpload } from './VideoUpload';
import { VideoPlayer } from './VideoPlayer';
import { CustomFieldsModal } from './CustomFieldsModal';
import { isProjectOwner } from '../../lib/permissions';
import { sanitizeFileName } from '../../lib/utils';

interface InsightsSectionProps {
  isOpen: boolean;
  onClose: () => void;
  projectId: string;
  customFields: any[];
  formData: {
    customerId: string;
    customerName: string;
    customerEmail: string;
    commsPlatform: string;
    customFieldValues: Record<string, any>;
    notes: string;
    helpfulScore: number;
    friendlyScore: number;
    willingToSpeakAgain: boolean;
    interviewId: string;
    interviewType: 'video' | 'text';
    videoStoragePath?: string | null;
  };
  onSubmit: (data: any) => Promise<void>;
  isEditing?: boolean;
  initialInsights?: any[];
  onSaveComplete?: () => void;
}

export function InterviewWorkspace({ 
  isOpen, 
  onClose, 
  projectId, 
  customFields: initialCustomFields,
  formData: initialFormData,
  onSubmit,
  isEditing = false,
  initialInsights = [],
  onSaveComplete
}: InsightsSectionProps) {
  const [customFields, setCustomFields] = useState(initialCustomFields);
  const [insights, setInsights] = useState(initialInsights);
  const [isAddingInsight, setIsAddingInsight] = useState(false);
  const [currentInsight, setCurrentInsight] = useState({
    title: '',
    description: '',
    start_timestamp: '',
    end_timestamp: '',
    screenshot_storage_path: null as string | null
  });
  const [interviewNotes, setInterviewNotes] = useState(initialFormData.notes || '');
  const [customFieldValues, setCustomFieldValues] = useState<Record<string, any>>(initialFormData.customFieldValues || {});
  const [showCustomFieldsModal, setShowCustomFieldsModal] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [canEdit, setCanEdit] = useState(false);
  const [customerFeedback, setCustomerFeedback] = useState({
    helpfulScore: 0,
    friendlyScore: 0,
    willingToSpeakAgain: null as boolean | null
  });
  const [videoStoragePath, setVideoStoragePath] = useState<string | null>(initialFormData.videoStoragePath || null);
  const [videoDuration, setVideoDuration] = useState<number>(0);
  const [uploadingScreenshot, setUploadingScreenshot] = useState(false);
  const [screenshotUrls, setScreenshotUrls] = useState<Record<string, string>>({});

  useEffect(() => {
    if (isEditing) {
      setVideoStoragePath(initialFormData.videoStoragePath || null);
    }
  }, [isEditing, initialFormData.videoStoragePath]);

  useEffect(() => {
    checkPermissions();
    if (isEditing) {
      fetchInterviewVideo();
      loadScreenshotUrls();
    }
    fetchCustomerFeedback();
  }, [projectId, isEditing]);

  async function fetchCustomerFeedback() {
    try {
      const { data, error } = await supabase
        .from('customers')
        .select('helpful_score, friendly_score, willing_to_speak_again')
        .eq('id', initialFormData.customerId)
        .single();

      if (error) throw error;
      if (data) {
        setCustomerFeedback({
          helpfulScore: data.helpful_score || 0,
          friendlyScore: data.friendly_score || 0,
          willingToSpeakAgain: data.willing_to_speak_again
        });
      }
    } catch (error) {
      console.error('Error fetching customer feedback:', error);
    }
  }

  async function loadScreenshotUrls() {
    const urlPromises = insights.map(async (insight) => {
      if (insight.screenshot_storage_path) {
        try {
          const { data, error } = await supabase.storage
            .from('interview_screenshots')
            .createSignedUrl(insight.screenshot_storage_path, 3600);

          if (error) throw error;
          return [insight.id, data.signedUrl];
        } catch (error) {
          console.error('Error getting signed URL:', error);
          return null;
        }
      }
      return null;
    });

    const urls = await Promise.all(urlPromises);
    const urlMap = Object.fromEntries(urls.filter(Boolean) as [string, string][]);
    setScreenshotUrls(urlMap);
  }

  async function fetchInterviewVideo() {
    try {
      const { data, error } = await supabase
        .from('interviews')
        .select('video_storage_path')
        .eq('id', initialFormData.interviewId)
        .single();

      if (error) throw error;
      if (data?.video_storage_path) {
        setVideoStoragePath(data.video_storage_path);
      }
    } catch (error) {
      console.error('Error fetching video path:', error);
    }
  }

  async function checkPermissions() {
    const isOwner = await isProjectOwner(projectId);
    setCanEdit(isOwner);
  }

  const handleVideoUpload = (path: string) => {
    console.log('Video uploaded, setting path:', path);
    setVideoStoragePath(path);
  };

  const handleCustomFieldsUpdate = (updatedFields: typeof initialCustomFields) => {
    setCustomFields(updatedFields);
  };

  const handleScreenshotUpload = async (file: File) => {
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      console.error('Invalid file type. Please upload an image.');
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      console.error('File size must be less than 5MB');
      return;
    }

    setUploadingScreenshot(true);
    try {
      const sanitizedFileName = sanitizeFileName(file.name);
      const filePath = `screenshots/${projectId}/${initialFormData.interviewId}/${sanitizedFileName}`;

      const { error: uploadError } = await supabase.storage
        .from('interview_screenshots')
        .upload(filePath, file, {
          cacheControl: '3600',
          upsert: true
        });

      if (uploadError) throw uploadError;

      const { data: urlData, error: urlError } = await supabase.storage
        .from('interview_screenshots')
        .createSignedUrl(filePath, 3600);

      if (urlError) throw urlError;

      setCurrentInsight(prev => ({
        ...prev,
        screenshot_storage_path: filePath
      }));

      setScreenshotUrls(prev => ({
        ...prev,
        'current': urlData.signedUrl
      }));
    } catch (error) {
      console.error('Error uploading screenshot:', error);
    } finally {
      setUploadingScreenshot(false);
    }
  };

  const handleAddInsight = async () => {
    if (!currentInsight.title) return;

    if (initialFormData.interviewType === 'video' && !isTimestampValid()) {
      return;
    }

    try {
      const insightData = {
        interview_id: initialFormData.interviewId,
        title: currentInsight.title,
        description: currentInsight.description || null,
        start_timestamp: initialFormData.interviewType === 'video' ? currentInsight.start_timestamp : null,
        end_timestamp: initialFormData.interviewType === 'video' ? currentInsight.end_timestamp || null : null,
        screenshot_storage_path: currentInsight.screenshot_storage_path
      };

      const { data, error } = await supabase
        .from('insights')
        .insert(insightData)
        .select()
        .single();

      if (error) throw error;

      if (data) {
        let signedUrl = null;
        if (data.screenshot_storage_path) {
          const { data: urlData } = await supabase.storage
            .from('interview_screenshots')
            .createSignedUrl(data.screenshot_storage_path, 3600);
          
          if (urlData) {
            signedUrl = urlData.signedUrl;
          }
        }

        setInsights(prevInsights => {
          const newInsights = [...prevInsights, data];
          if (initialFormData.interviewType === 'video') {
            return newInsights.sort((a, b) => {
              if (!a.start_timestamp || !b.start_timestamp) return 0;
              const aTime = a.start_timestamp.split(':').map(Number);
              const bTime = b.start_timestamp.split(':').map(Number);
              const aSeconds = aTime[0] * 3600 + aTime[1] * 60 + aTime[2];
              const bSeconds = bTime[0] * 3600 + bTime[1] * 60 + bTime[2];
              return aSeconds - bSeconds;
            });
          }
          return newInsights;
        });

        if (signedUrl) {
          setScreenshotUrls(prev => ({
            ...prev,
            [data.id]: signedUrl
          }));
        }
      }
      
      setCurrentInsight({
        title: '',
        description: '',
        start_timestamp: '',
        end_timestamp: '',
        screenshot_storage_path: null
      });
      setIsAddingInsight(false);
    } catch (error) {
      console.error('Error saving insight:', error);
    }
  };

  const isTimestampValid = () => {
    if (!currentInsight.start_timestamp) return false;
    const [hours, minutes, seconds] = currentInsight.start_timestamp.split(':').map(Number);
    return !isNaN(hours) && !isNaN(minutes) && !isNaN(seconds);
  };

  const handleFinish = async () => {
    setIsSaving(true);
    try {
      console.log('Saving interview with video path:', videoStoragePath);
      
      const updateData = {
        notes: interviewNotes,
        customFieldValues: customFieldValues,
        videoStoragePath: videoStoragePath,
        videoFilename: videoStoragePath ? videoStoragePath.split('/').pop() : null,
        videoContentType: 'video/mp4',
        videoUploadedAt: videoStoragePath ? new Date().toISOString() : null
      };

      console.log('Submitting update data:', updateData);
      
      await onSubmit(updateData);
      
      onClose();
      onSaveComplete?.();
    } catch (error) {
      console.error('Error saving interview:', error);
    } finally {
      setIsSaving(false);
    }
  };

  const handleFieldChange = (field: any, value: any) => {
    if (field.field_type === 'multi_select') {
      const currentValues = customFieldValues[field.id] || [];
      const newValues = currentValues.includes(value) 
        ? currentValues.filter((v: string) => v !== value)
        : [...currentValues, value];
      
      setCustomFieldValues(prev => ({
        ...prev,
        [field.id]: newValues
      }));
    } else {
      setCustomFieldValues(prev => ({
        ...prev,
        [field.id]: value
      }));
    }
  };

  const renderStarRating = (value: number, onChange: (value: number) => void) => (
    <div className="flex items-center space-x-1">
      {[1, 2, 3, 4, 5].map((star) => (
        <button
          key={star}
          onClick={() => onChange(star)}
          className="p-1 focus:outline-none"
        >
          <Star 
            className={`h-5 w-5 ${
              star <= value 
                ? 'text-yellow-400 fill-current' 
                : 'text-gray-300'
            }`}
          />
        </button>
      ))}
    </div>
  );

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 bg-white">
      <div className="h-16 border-b border-gray-200 px-4 flex items-center justify-between bg-white">
        <h2 className="text-lg font-medium text-brand-teal">
          {isEditing ? 'Edit Interview' : 'New Interview'}
        </h2>
        <div className="flex items-center space-x-4">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-brand-orange transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleFinish}
            disabled={isSaving || isUploading}
            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-lg text-white bg-brand-orange hover:bg-brand-orange-light focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-brand-orange transition-colors disabled:opacity-50"
          >
            <Save className="w-4 h-4 mr-2" />
            {isSaving ? 'Saving...' : 'Save Interview'}
          </button>
        </div>
      </div>

      <div className="h-[calc(100vh-4rem)] flex overflow-hidden">
        <div className="w-1/2 border-r border-gray-200 overflow-y-auto p-6 space-y-8">
          {initialFormData.interviewType === 'video' && (
            <div className="space-y-4">
              <h3 className="text-sm font-medium text-gray-700">
                Interview Recording
              </h3>
              
              {videoStoragePath ? (
                <VideoPlayer 
                  projectId={projectId} 
                  storagePath={videoStoragePath}
                  onDurationChange={setVideoDuration}
                />
              ) : (
                <VideoUpload
                  projectId={projectId}
                  interviewId={initialFormData.interviewId}
                  onUploadComplete={handleVideoUpload}
                  onUploadStateChange={setIsUploading}
                />
              )}
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Interview Notes
            </label>
            <textarea
              value={interviewNotes}
              onChange={(e) => setInterviewNotes(e.target.value)}
              rows={6}
              className="block w-full rounded-lg border-gray-300 shadow-sm focus:ring-2 focus:ring-brand-orange focus:border-brand-orange transition-colors"
              placeholder="Add any general notes about the interview..."
            />
          </div>

          <div className="bg-gray-50 rounded-lg p-6 space-y-6">
            <h3 className="text-sm font-medium text-gray-700">Customer Feedback</h3>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  How helpful was this customer?
                </label>
                {renderStarRating(
                  customerFeedback.helpfulScore,
                  (value) => setCustomerFeedback(prev => ({ ...prev, helpfulScore: value }))
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  How friendly was this customer?
                </label>
                {renderStarRating(
                  customerFeedback.friendlyScore,
                  (value) => setCustomerFeedback(prev => ({ ...prev, friendlyScore: value }))
                )}
              </div>

              <div>
                <label className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    checked={customerFeedback.willingToSpeakAgain === true}
                    onChange={(e) => setCustomerFeedback(prev => ({ 
                      ...prev, 
                      willingToSpeakAgain: e.target.checked 
                    }))}
                    className="rounded border-gray-300 text-brand-orange focus:ring-brand-orange"
                  />
                  <span className="text-sm font-medium text-gray-700">
                    Willing to speak again
                  </span>
                </label>
              </div>
            </div>
          </div>

          <div>
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-sm font-medium text-gray-700">Custom Fields</h3>
              <button
                onClick={() => setShowCustomFieldsModal(true)}
                className="text-sm text-brand-orange hover:text-brand-orange-light transition-colors"
              >
                Manage Fields
              </button>
            </div>

            <div className="bg-white rounded-lg shadow-sm border border-gray-200 divide-y divide-gray-200">
              {customFields.map((field) => (
                <div key={field.id} className="p-4">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {field.name}
                  </label>
                  {field.field_type === 'text' && (
                    <input
                      type="text"
                      value={customFieldValues[field.id] || ''}
                      onChange={(e) => handleFieldChange(field, e.target.value)}
                      className="block w-full rounded-lg border-gray-300 shadow-sm focus:ring-2 focus:ring-brand-orange focus:border-brand-orange transition-colors"
                    />
                  )}
                  {field.field_type === 'number' && (
                    <input
                      type="number"
                      value={customFieldValues[field.id] || ''}
                      onChange={(e) => handleFieldChange(field, e.target.value)}
                      className="block w-full rounded-lg border-gray-300 shadow-sm focus:ring-2 focus:ring-brand-orange focus:border-brand-orange transition-colors"
                    />
                  )}
                  {field.field_type === 'boolean' && (
                    <input
                      type="checkbox"
                      checked={customFieldValues[field.id] || false}
                      onChange={(e) => handleFieldChange(field, e.target.checked)}
                      className="rounded border-gray-300 text-brand-orange focus:ring-brand-orange"
                    />
                  )}
                  {field.field_type === 'date' && (
                    <input
                      type="date"
                      value={customFieldValues[field.id] || ''}
                      onChange={(e) => handleFieldChange(field, e.target.value)}
                      className="block w-full rounded-lg border-gray-300 shadow-sm focus:ring-2 focus:ring-brand-orange focus:border-brand-orange transition-colors"
                    />
                  )}
                  {field.field_type === 'single_select' && (
                    <select
                      value={customFieldValues[field.id] || ''}
                      onChange={(e) => handleFieldChange(field, e.target.value)}
                      className="block w-full rounded-lg border-gray-300 shadow-sm focus:ring-2 focus:ring-brand-orange focus:border-brand-orange transition-colors"
                    >
                      <option value="">Select an option</option>
                      {field.options?.map((option: string) => (
                        <option key={option} value={option}>
                          {option}
                        </option>
                      ))}
                    </select>
                  )}
                  {field.field_type === 'multi_select' && (
                    <div className="space-y-2">
                      {field.options?.map((option: string) => (
                        <label key={option} className="flex items-center">
                          <input
                            type="checkbox"
                            checked={(customFieldValues[field.id] || []).includes(option)}
                            onChange={() => handleFieldChange(field, option)}
                            className="rounded border-gray-300 text-brand-orange focus:ring-brand-orange"
                          />
                          <span className="ml-2 text-sm text-gray-700">{option}</span>
                        </label>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="w-1/2 overflow-y-auto p-6">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-sm font-medium text-gray-700">Key Insights</h3>
            <button
              onClick={() => setIsAddingInsight(true)}
              className="inline-flex items-center text-sm text-brand-orange hover:text-brand-orange-light transition-colors"
            >
              <PlusCircle className="w-4 h-4 mr-1" />
              Add Insight
            </button>
          </div>

          {isAddingInsight && (
            <div className="mb-6 bg-white p-4 rounded-lg shadow-sm border border-gray-200">
              <input
                type="text"
                value={currentInsight.title}
                onChange={(e) => setCurrentInsight(prev => ({ ...prev, title: e.target.value }))}
                placeholder="Insight title"
                className="block w-full rounded-lg border-gray-300 shadow-sm focus:ring-2 focus:ring-brand-orange focus:border-brand-orange transition-colors mb-3"
              />
              <textarea
                value={currentInsight.description}
                onChange={(e) => setCurrentInsight(prev => ({ ...prev, description: e.target.value }))}
                placeholder="Description (optional)"
                rows={3}
                className="block w-full rounded-lg border-gray-300 shadow-sm focus:ring-2 focus:ring-brand-orange focus:border-brand-orange transition-colors mb-3"
              />
              
              {initialFormData.interviewType === 'video' ? (
                <div className="flex space-x-4 mb-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Start Time
                    </label>
                    <input
                      type="text"
                      value={currentInsight.start_timestamp}
                      onChange={(e) => setCurrentInsight(prev => ({ ...prev, start_timestamp: e.target.value }))}
                      placeholder="00:00:00"
                      className="block w-32 rounded-lg border-gray-300 shadow-sm focus:ring-2 focus:ring-brand-orange focus:border-brand-orange transition-colors"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      End Time
                    </label>
                    <input
                      type="text"
                      value={currentInsight.end_timestamp}
                      onChange={(e) => setCurrentInsight(prev => ({ ...prev, end_timestamp: e.target.value }))}
                      placeholder="00:00:00"
                      className="block w-32 rounded-lg border-gray-300 shadow-sm focus:ring-2 focus:ring-brand-orange focus:border-brand-orange transition-colors"
                    />
                  </div>
                </div>
              ) : (
                <div className="mb-3">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Screenshot (optional)
                  </label>
                  <div className="mt-1 flex items-center">
                    <label className="relative cursor-pointer bg-white rounded-md font-medium text-brand-orange hover:text-brand-orange-light focus-within:outline-none focus-within:ring-2 focus-within:ring-offset-2 focus-within:ring-brand-orange">
                      <span className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-brand-orange">
                        <Upload className="w-4 h-4 mr-2" />
                        Upload Screenshot
                      </span>
                      <input
                        type="file"
                        className="sr-only"
                        accept="image/*"
                        onChange={(e) => {
                          const file = e.target.files?.[0];
                          if (file) {
                            handleScreenshotUpload(file);
                          }
                        }}
                        disabled={uploadingScreenshot}
                      />
                    </label>
                    {uploadingScreenshot && (
                      <span className="ml-3 text-sm text-gray-500">
                        Uploading...
                      </span>
                    )}
                    {currentInsight.screenshot_storage_path && (
                      <span className="ml-3 text-sm text-green-600">
                        Screenshot uploaded
                      </span>
                    )}
                  </div>
                  {currentInsight.screenshot_storage_path && screenshotUrls['current'] && (
                    <div className="mt-2">
                      <img
                        src={screenshotUrls['current']}
                        alt="Screenshot preview"
                        className="max-w-full h-auto rounded-lg border border-gray-200"
                      />
                    </div>
                  )}
                </div>
              )}

              <div className="flex justify-end space-x-3">
                <button
                  onClick={() => {
                    setCurrentInsight({
                      title: '',
                      description: '',
                      start_timestamp: '',
                      end_timestamp: '',
                      screenshot_storage_path: null
                    });
                    setIsAddingInsight(false);
                  }}
                  className="px-3 py-2 text-sm text-gray-700 hover:text-gray-900"
                >
                  Cancel
                </button>
                <button
                  onClick={handleAddInsight}
                  disabled={!currentInsight.title || (initialFormData.interviewType === 'video' && !isTimestampValid())}
                  className="px-3 py-2 text-sm text-white bg-brand-orange hover:bg-brand-orange-light rounded-lg disabled:opacity-50"
                >
                  Add Insight
                </button>
              </div>
            </div>
          )}

          <div className="space-y-4">
            {insights.map((insight) => (
              <div key={insight.id} className="bg-white p-4 rounded-lg shadow-sm border border-gray-200">
                <div className="flex justify-between items-start">
                  <div>
                    <h4 className="font-medium text-gray-900">{insight.title}</h4>
                    {insight.description && (
                      <p className="mt-1 text-sm text-gray-600">{insight.description}</p>
                    )}
                    {initialFormData.interviewType === 'video' && insight.start_timestamp && (
                      <div className="mt-2 text-xs text-gray-400">
                        {insight.start_timestamp}{insight.end_timestamp ? ` - ${insight.end_timestamp}` : ''}
                      </div>
                    )}
                    {insight.screenshot_storage_path && screenshotUrls[insight.id] && (
                      <div className="mt-2">
                        <img
                          src={screenshotUrls[insight.id]}
                          alt="Insight screenshot"
                          className="max-w-full h-auto rounded-lg border border-gray-200"
                        />
                      </div>
                    )}
                  </div>
                  <button
                    onClick={() => {
                      setInsights(insights.filter(i => i.id !== insight.id));
                    }}
                    className="text-red-600 hover:text-red-900 transition-colors"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {showCustomFieldsModal && (
        <CustomFieldsModal
          isOpen={showCustomFieldsModal}
          onClose={() => setShowCustomFieldsModal(false)}
          projectId={projectId}
          existingFields={customFields}
          onFieldsUpdate={handleCustomFieldsUpdate}
        />
      )}
    </div>
  );
}